// MemberForm v2 - mit Eltern-Validierung
import React, { useState, useEffect, useRef } from 'react';
import { MEMBERSHIP_OPTIONS } from './membershipTypes';
import { useAuth } from './AuthContext';

const SEPA_REQUIRED_TYPES = ['entdecker', 'digital_pilot', 'astronaut'];
const WEEKDAY_LABELS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

function MemberForm({ member, onSave, onCancel }) {
  const { authFetch } = useAuth();
  const [formData, setFormData] = useState({
    // Kind-Daten
    first_name: '',
    last_name: '',
    birth_date: '',
    school: '',
    interests: '',
    allergies: '',
    // Eltern-Daten
    parent_salutation: '',
    parent_first_name: '',
    parent_last_name: '',
    email: '',
    phone: '',
    street: '',
    postal_code: '',
    city: '',
    // Mitgliedschaft
    membership_type: '',
    start_date: '',
    notes: '',
    // SEPA-Lastschriftmandat
    sepa_kontoinhaber: '',
    sepa_iban: '',
    sepa_bic: '',
    sepa_accepted: false,
    // Einverstaendnisse
    photo_consent: false,
    newsletter_consent: false,
    // Berechtigungen
    internetfuehrerschein_enabled: false,
    // Login
    username: '',
    password: '',
    // PDF
    application_pdf: ''
  });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  // Kinder-Verwaltung
  const [children, setChildren] = useState([]);
  const [newChild, setNewChild] = useState({ first_name: '', age: '', school: '', allergies: '' });
  const [showAddChild, setShowAddChild] = useState(false);

  // Feriencamp-Freischaltungen
  const [feriencamps, setFeriencamps] = useState([]);
  const [selectedFeriencamps, setSelectedFeriencamps] = useState([]);

  // Kurs-Freischaltungen (dauerhaft sichtbar unter "Mein Bereich", unabhaengig von Lernfortschritt-Notizen)
  const [courses, setCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);

  const requiresSepa = SEPA_REQUIRED_TYPES.includes(formData.membership_type);

  // Passwort generieren: 1 Grossbuchstabe + 6 Ziffern + 1 Sonderzeichen
  const generatePassword = () => {
    const upperCase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const digits = '0123456789';
    const special = '!@#$%&*';
    let pwd = '';
    pwd += upperCase.charAt(Math.floor(Math.random() * upperCase.length));
    for (let i = 0; i < 6; i++) {
      pwd += digits.charAt(Math.floor(Math.random() * digits.length));
    }
    pwd += special.charAt(Math.floor(Math.random() * special.length));
    return pwd;
  };

  // Benutzername generieren: KindnameRhino
  const generateUsername = (childName) => {
    if (!childName) return '';
    const cleanName = childName.replace(/[^a-zA-Z]/g, '');
    return cleanName + 'Rhino';
  };

  // Auto-generiere Login-Daten wenn erstes Kind hinzugefuegt wird
  const updateLoginFromChild = (childName) => {
    if (!member && childName && !formData.username) {
      setFormData(prev => ({
        ...prev,
        username: generateUsername(childName),
        password: prev.password || generatePassword()
      }));
    }
  };

  useEffect(() => {
    if (member) {
      setFormData({
        first_name: member.first_name || '',
        last_name: member.last_name || '',
        birth_date: member.birth_date || '',
        school: member.school || '',
        interests: member.interests || '',
        allergies: member.allergies || '',
        parent_salutation: member.parent_salutation || '',
        parent_first_name: member.parent_first_name || '',
        parent_last_name: member.parent_last_name || '',
        email: member.email || '',
        phone: member.phone || '',
        street: member.street || '',
        postal_code: member.postal_code || '',
        city: member.city || '',
        membership_type: member.membership_type || '',
        start_date: member.start_date || '',
        notes: member.notes || '',
        sepa_kontoinhaber: member.sepa_kontoinhaber || '',
        sepa_iban: member.sepa_iban || '',
        sepa_bic: member.sepa_bic || '',
        sepa_accepted: member.sepa_accepted || false,
        photo_consent: member.photo_consent || false,
        newsletter_consent: member.newsletter_consent || false,
        internetfuehrerschein_enabled: member.internetfuehrerschein_enabled || false,
        username: member.username || '',
        password: '',
        application_pdf: member.application_pdf || ''
      });
      // Kinder laden falls vorhanden
      if (member.children) {
        setChildren(member.children);
      } else if (member.id) {
        fetchChildren(member.id);
      }
      // Feriencamp-Freischaltungen laden
      if (member.id) {
        fetchFeriencampAccess(member.id);
      }
      // Kurs-Freischaltungen laden
      if (member.id) {
        fetchCourseAccess(member.id);
      }
    }
  }, [member]);

  // Feriencamps und Kurse beim Start laden
  useEffect(() => {
    fetchFeriencamps();
    fetchCourses();
  }, []);

  const fetchFeriencamps = async () => {
    try {
      const response = await fetch('/api/feriencamp-events');
      const result = await response.json();
      if (result.data) {
        setFeriencamps(result.data);
      }
    } catch (err) {
      console.error('Error fetching feriencamps:', err);
    }
  };

  const fetchFeriencampAccess = async (memberId) => {
    try {
      const response = await authFetch(`/api/members/${memberId}/feriencamp-access`);
      const result = await response.json();
      if (result.data) {
        setSelectedFeriencamps(result.data);
      }
    } catch (err) {
      console.error('Error fetching feriencamp access:', err);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await fetch('/api/courses');
      const result = await response.json();
      if (result.data) {
        setCourses(result.data);
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchCourseAccess = async (memberId) => {
    try {
      const response = await authFetch(`/api/members/${memberId}/course-access`);
      const result = await response.json();
      if (result.data) {
        setSelectedCourses(result.data);
      }
    } catch (err) {
      console.error('Error fetching course access:', err);
    }
  };

  const fetchChildren = async (memberId) => {
    try {
      const response = await authFetch(`/api/members/${memberId}/children`);
      const result = await response.json();
      if (result.data) {
        setChildren(result.data);
      }
    } catch (err) {
      console.error('Error fetching children:', err);
    }
  };

  const handleAddChild = async () => {
    if (!newChild.first_name.trim()) {
      setError('Vorname des Kindes ist erforderlich');
      return;
    }

    if (member?.id) {
      // Bestehendes Mitglied - direkt in DB speichern
      try {
        const response = await authFetch(`/api/members/${member.id}/children`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...newChild, age: newChild.age === '' ? null : Number(newChild.age) })
        });
        const result = await response.json();
        if (response.ok) {
          setChildren([...children, result.data]);
          setNewChild({ first_name: '', age: '', school: '', allergies: '' });
          setShowAddChild(false);
        } else {
          setError(result.error || 'Fehler beim Hinzufuegen');
        }
      } catch (err) {
        setError('Netzwerkfehler');
      }
    } else {
      // Neues Mitglied - lokal speichern
      const newChildren = [...children, { ...newChild, id: Date.now() }];
      setChildren(newChildren);

      // Login-Daten generieren wenn erstes Kind
      if (newChildren.length === 1) {
        updateLoginFromChild(newChild.first_name);
      }

      setNewChild({ first_name: '', age: '', school: '', allergies: '' });
      setShowAddChild(false);
    }
  };

  const handleDeleteChild = async (childId) => {
    if (!window.confirm('Kind wirklich entfernen?')) return;

    if (member?.id) {
      try {
        const response = await authFetch(`/api/members/${member.id}/children/${childId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          setChildren(children.filter(c => c.id !== childId));
        }
      } catch (err) {
        setError('Fehler beim Loeschen');
      }
    } else {
      setChildren(children.filter(c => c.id !== childId));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // PDF-Import: Laedt Daten aus PDF
  const handlePdfImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setError('');
    setSuccess('');

    const formDataUpload = new FormData();
    formDataUpload.append('pdf', file);

    try {
      const response = await authFetch('/api/members/import-pdf', {
        method: 'POST',
        body: formDataUpload
      });

      const result = await response.json();

      if (response.ok && result.data) {
        // Merge imported data with existing form data
        setFormData(prev => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(result.data).filter(([_, v]) => v !== null && v !== undefined && v !== '')
          )
        }));

        // Prüfe ob es eine Warnung gibt (z.B. gescanntes PDF)
        if (result.warning) {
          setError(result.warning);
        } else {
          setSuccess('PDF erfolgreich importiert! Bitte Daten prüfen.');
        }
      } else {
        setError(result.error || 'Fehler beim PDF-Import');
      }
    } catch (err) {
      setError('Netzwerkfehler beim PDF-Import');
      console.error('PDF Import Error:', err);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // PDF-Download
  const handlePdfDownload = async () => {
    if (!member?.id) return;

    try {
      const response = await authFetch(`/api/members/${member.id}/pdf`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Mitgliedschaftsantrag_${formData.first_name}_${formData.last_name}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        setError('Kein PDF vorhanden');
      }
    } catch (err) {
      setError('Fehler beim PDF-Download');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Bei neuen Mitgliedern: Elterndaten erforderlich
    // Bei bestehenden Mitgliedern (erkennbar an member oder member.id): nur Warnung, kein Blocker
    const isExistingMember = member && member.id;
    if (!formData.parent_first_name?.trim() || !formData.parent_last_name?.trim()) {
      if (!isExistingMember) {
        setError('Vorname und Nachname (Elternteil) sind erforderlich.');
        return;
      }
      // Bei Bearbeitung: Warnung anzeigen, aber nicht blockieren
      console.warn('Elterndaten unvollständig - wird trotzdem gespeichert');
    }

    // Bei neuen Mitgliedern: mind. 1 Kind erforderlich
    // Bei bestehenden Mitgliedern: erlauben (z.B. importierte Feriencamp-Buchungen)
    if (children.length === 0 && !member) {
      setError('Bitte mindestens ein Kind hinzufuegen.');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen haben.');
      return;
    }

    if (formData.username && !formData.password && !member) {
      setError('Wenn ein Benutzername vergeben wird, muss auch ein Passwort gesetzt werden.');
      return;
    }

    // Bei neuen Mitgliedern mit SEPA-pflichtiger Mitgliedschaft: Lastschriftmandat erforderlich
    if (requiresSepa && !isExistingMember) {
      if (!formData.sepa_kontoinhaber?.trim() || !formData.sepa_iban?.trim() || !formData.sepa_accepted) {
        setError('Fuer diese Mitgliedschaft werden Kontoinhaber, IBAN und die SEPA-Zustimmung benoetigt.');
        return;
      }
    }

    setSaving(true);
    try {
      const url = member ? `/api/members/${member.id}` : '/api/members';
      const method = member ? 'PUT' : 'POST';

      // Setze first_name/last_name (Backend erwartet diese Felder)
      const dataToSend = { ...formData };
      if (children.length > 0) {
        // Aus erstem Kind
        dataToSend.first_name = children[0].first_name || formData.first_name || formData.parent_first_name || '';
        dataToSend.last_name = children[0].last_name || formData.last_name || formData.parent_last_name || '';
      } else {
        // Fallback: bestehende Werte oder Elterndaten verwenden
        dataToSend.first_name = formData.first_name || formData.parent_first_name || '';
        dataToSend.last_name = formData.last_name || formData.parent_last_name || '';
      }

      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });

      const result = await response.json();

      if (response.ok) {
        const memberId = result.data.id;

        // Feriencamp- und Kurs-Freischaltungen speichern
        if (memberId) {
          await authFetch(`/api/members/${memberId}/feriencamp-access`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ feriencamp_ids: selectedFeriencamps })
          });
          await authFetch(`/api/members/${memberId}/course-access`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ course_ids: selectedCourses })
          });
        }

        onSave(result.data);
      } else {
        setError(result.error || 'Fehler beim Speichern');
      }
    } catch (error) {
      setError('Netzwerkfehler beim Speichern');
      console.error('Error saving member:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          {member ? 'Mitglied bearbeiten' : 'Neues Mitglied anlegen'}
        </h5>
        <div className="d-flex gap-2">
          <label className="btn btn-outline-primary btn-sm mb-0">
            {importing ? 'Importiere...' : 'PDF importieren'}
            <input
              type="file"
              accept=".pdf"
              onChange={handlePdfImport}
              ref={fileInputRef}
              style={{ display: 'none' }}
              disabled={importing}
            />
          </label>
          {member?.application_pdf && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handlePdfDownload}
            >
              PDF herunterladen
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Mitgliedschaft */}
          <div className="card mb-4 bg-light">
            <div className="card-body">
              <h6 className="card-title">1. Mitgliedschaft</h6>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Mitgliedschaftsart</label>
                  <select
                    className="form-select"
                    name="membership_type"
                    value={formData.membership_type}
                    onChange={handleChange}
                  >
                    <option value="">-- Bitte waehlen --</option>
                    {MEMBERSHIP_OPTIONS.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name} ({type.price} {type.priceType !== 'einmalig' ? '/ ' + type.priceType : type.priceType})
                        {type.recommended ? ' - Empfehlung' : ''}
                      </option>
                    ))}
                  </select>
                  {formData.membership_type && (
                    <div className="form-text">
                      {MEMBERSHIP_OPTIONS.find(t => t.id === formData.membership_type)?.highlight}
                    </div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Startdatum</label>
                  <input
                    type="date"
                    className="form-control"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Kinder */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title mb-0">2. Kinder</h6>
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={() => setShowAddChild(true)}
                >
                  + Kind hinzufuegen
                </button>
              </div>

              {children.length === 0 && !showAddChild && (
                <p className="text-muted">Noch keine Kinder hinzugefuegt.</p>
              )}

              {/* Liste der Kinder */}
              {children.map((child, index) => (
                <div key={child.id} className="card mb-2 bg-light">
                  <div className="card-body py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{child.first_name}</strong>
                        {child.last_name && ` ${child.last_name}`}
                        {child.age && <span className="text-muted ms-2">({child.age} Jahre)</span>}
                        {child.school && <small className="text-muted d-block">{child.school}</small>}
                        {child.allergies && <small className="text-danger d-block">Allergien: {child.allergies}</small>}
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteChild(child.id)}
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Formular zum Hinzufuegen */}
              {showAddChild && (
                <div className="card border-success mt-3">
                  <div className="card-body">
                    <h6 className="card-title">Neues Kind hinzufuegen</h6>
                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Vorname *"
                          value={newChild.first_name}
                          onChange={(e) => setNewChild({ ...newChild, first_name: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3 mb-2">
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="Alter"
                          value={newChild.age}
                          onChange={(e) => setNewChild({ ...newChild, age: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3 mb-2">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Schule"
                          value={newChild.school}
                          onChange={(e) => setNewChild({ ...newChild, school: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Allergien / Besonderheiten"
                        value={newChild.allergies}
                        onChange={(e) => setNewChild({ ...newChild, allergies: e.target.value })}
                      />
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-success"
                        onClick={handleAddChild}
                      >
                        Hinzufuegen
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setShowAddChild(false);
                          setNewChild({ first_name: '', age: '', school: '', allergies: '' });
                        }}
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Erziehungsberechtigte/r */}
          <div className="card mb-4">
            <div className="card-body">
              <h6 className="card-title">3. Erziehungsberechtigte/r</h6>
              <div className="row">
                <div className="col-md-2 mb-3">
                  <label className="form-label">Anrede</label>
                  <select
                    className="form-select"
                    name="parent_salutation"
                    value={formData.parent_salutation}
                    onChange={handleChange}
                  >
                    <option value="">--</option>
                    <option value="Frau">Frau</option>
                    <option value="Herr">Herr</option>
                    <option value="Divers">Divers</option>
                  </select>
                </div>
                <div className="col-md-5 mb-3">
                  <label className="form-label">Vorname</label>
                  <input
                    type="text"
                    className="form-control"
                    name="parent_first_name"
                    value={formData.parent_first_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-5 mb-3">
                  <label className="form-label">Nachname</label>
                  <input
                    type="text"
                    className="form-control"
                    name="parent_last_name"
                    value={formData.parent_last_name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Strasse & Hausnummer</label>
                <input
                  type="text"
                  className="form-control"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                />
              </div>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">PLZ</label>
                  <input
                    type="text"
                    className="form-control"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-8 mb-3">
                  <label className="form-label">Ort</label>
                  <input
                    type="text"
                    className="form-control"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">E-Mail</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Telefon</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* SEPA-Lastschriftmandat */}
          <div className={`card mb-4 ${requiresSepa ? 'border-primary' : ''}`}>
            <div className="card-body">
              <h6 className="card-title">4. SEPA-Lastschriftmandat</h6>
              <p className="text-muted small">
                Ermaechtigung, Zahlungen fuer diese Mitgliedschaft per Lastschrift einzuziehen.
                {requiresSepa ? ' Fuer die gewaehlte Mitgliedschaft erforderlich.' : ' Optional.'}
              </p>
              <div className="mb-3">
                <label className="form-label">Kontoinhaber{requiresSepa ? ' *' : ''}</label>
                <input
                  type="text"
                  className="form-control"
                  name="sepa_kontoinhaber"
                  value={formData.sepa_kontoinhaber}
                  onChange={handleChange}
                  required={requiresSepa && !(member && member.id)}
                />
              </div>
              <div className="row">
                <div className="col-md-8 mb-3">
                  <label className="form-label">IBAN{requiresSepa ? ' *' : ''}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="sepa_iban"
                    placeholder="DE00 0000 0000 0000 0000 00"
                    value={formData.sepa_iban}
                    onChange={handleChange}
                    required={requiresSepa && !(member && member.id)}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">BIC (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="sepa_bic"
                    value={formData.sepa_bic}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="sepa_accepted"
                  name="sepa_accepted"
                  checked={formData.sepa_accepted}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="sepa_accepted">
                  SEPA-Lastschrifteinzug wurde ermaechtigt{requiresSepa ? ' *' : ''}
                </label>
              </div>
            </div>
          </div>

          {/* Einverstaendnisse */}
          <div className="card mb-4">
            <div className="card-body">
              <h6 className="card-title">5. Einverstaendnisse</h6>
              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="photo_consent"
                  name="photo_consent"
                  checked={formData.photo_consent}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="photo_consent">
                  Ich erlaube Fotos/Videos meines Kindes fuer Oeffentlichkeitsarbeit
                </label>
              </div>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="newsletter_consent"
                  name="newsletter_consent"
                  checked={formData.newsletter_consent}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="newsletter_consent">
                  Ich moechte den Newsletter erhalten
                </label>
              </div>
            </div>
          </div>

          {/* Berechtigungen */}
          <div className="card mb-4 border-info">
            <div className="card-body">
              <h6 className="card-title">6. Berechtigungen</h6>
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="internetfuehrerschein_enabled"
                  name="internetfuehrerschein_enabled"
                  checked={formData.internetfuehrerschein_enabled}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="internetfuehrerschein_enabled">
                  <strong>Internetfuehrerschein freigeschaltet</strong>
                  <small className="text-muted d-block">Mitglied darf den kleinen Internetfuehrerschein machen</small>
                </label>
              </div>

              {/* Feriencamp-Freischaltungen */}
              {feriencamps.length > 0 && (
                <div className="mt-3">
                  <label className="form-label"><strong>Feriencamps freigeschaltet:</strong></label>
                  <small className="text-muted d-block mb-2">Mitglied kann nur die ausgewaehlten Feriencamps sehen und buchen</small>
                  <div className="row">
                    {feriencamps.map(camp => (
                      <div key={camp.id} className="col-md-6 mb-2">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`feriencamp_${camp.id}`}
                            checked={selectedFeriencamps.includes(camp.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedFeriencamps([...selectedFeriencamps, camp.id]);
                              } else {
                                setSelectedFeriencamps(selectedFeriencamps.filter(id => id !== camp.id));
                              }
                            }}
                          />
                          <label className="form-check-label" htmlFor={`feriencamp_${camp.id}`}>
                            {camp.title}
                            <small className="text-muted d-block">
                              {new Date(camp.start_date).toLocaleDateString('de-DE')} - {new Date(camp.end_date).toLocaleDateString('de-DE')}
                            </small>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Kurs-Freischaltungen */}
              {courses.length > 0 && (
                <div className="mt-3">
                  <label className="form-label"><strong>Kurse freigeschaltet:</strong></label>
                  <small className="text-muted d-block mb-2">
                    Mitglied sieht diese Kurse dauerhaft unter "Mein Bereich" - unabhaengig von Lernfortschritt-Notizen
                  </small>
                  <div className="row">
                    {courses.map(course => (
                      <div key={course.id} className="col-md-6 mb-2">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`course_${course.id}`}
                            checked={selectedCourses.includes(course.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedCourses([...selectedCourses, course.id]);
                              } else {
                                setSelectedCourses(selectedCourses.filter(id => id !== course.id));
                              }
                            }}
                          />
                          <label className="form-check-label" htmlFor={`course_${course.id}`}>
                            {course.name}
                            {course.slots?.length > 0 && (
                              <small className="text-muted d-block">
                                {course.slots.map(s => `${WEEKDAY_LABELS[s.weekday]} ${s.time.slice(0, 5)}`).join(', ')}
                              </small>
                            )}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notizen */}
          <div className="mb-4">
            <label className="form-label">Interne Notizen</label>
            <textarea
              className="form-control"
              name="notes"
              rows="3"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Interne Notizen (nicht sichtbar fuer das Mitglied)"
            />
          </div>

          {/* Login-Daten */}
          <div className="card mb-4 border-primary">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title mb-0">Login-Daten</h6>
                {!formData.username && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={() => {
                      const childName = children[0]?.first_name || formData.first_name || 'User';
                      setFormData(prev => ({
                        ...prev,
                        username: generateUsername(childName),
                        password: generatePassword()
                      }));
                    }}
                  >
                    Automatisch generieren
                  </button>
                )}
              </div>
              <p className="text-muted small mb-3">
                Benutzername: KindnameRhino | Passwort: Grossbuchstabe + 6 Ziffern + Sonderzeichen
              </p>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Benutzername</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="z.B. BenediktRhino"
                    />
                    {!formData.username && children.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setFormData(prev => ({ ...prev, username: generateUsername(children[0].first_name) }))}
                        title="Aus Kindname generieren"
                      >
                        Gen
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    {member && formData.username ? 'Neues Passwort (leer lassen um beizubehalten)' : 'Passwort'}
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder={member ? 'Nur ausfuellen um zu aendern' : 'z.B. A123456!'}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setFormData(prev => ({ ...prev, password: generatePassword() }))}
                      title="Passwort generieren"
                    >
                      Gen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex gap-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Speichern...' : (member ? 'Aktualisieren' : 'Anlegen')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={saving}
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MemberForm;
